<template>
  <!-- the top margin and the content's left pad below both exist to clear the
       in-page nav rail. horizon/midnight don't render that rail (its
       destinations live in the section nav), so there they are dead space and
       these pages sat ~52px further right than every other horizon page. -->
  <div class="alerts-shell" :class="{ 'alerts-shell--flush': horizonNavActive }">

    <!-- Marketing pitch for tenants without Alerts access -->
    <feature-upsell
      v-if="showMarketingPage"
      icon="fa-bell"
      headline="Alerts &amp; Notifications"
      sub="Alerts &amp; Notifications isn't enabled for your account yet. It keeps your team ahead of dispatch issues with automated alerts by email, SMS, and voice."
      :items="upsellItems"
      category="alerts"
      subject="I'd like to sign up for Alerts & Notifications"
    />

    <!-- Nav shell for tenants with access: Dashboard + Alert History -->
    <div v-else class="as-layout">
      <!-- the horizon/midnight chrome already lists these destinations in its
           section nav (see registry.js 'status' group), so the in-page rail
           only renders under classic - mirrors booking_shell.vue:6 -->
      <div v-if="!horizonNavActive" class="as-nav-wrapper" :class="{ 'as-nav-collapsed': navCollapsed }">
        <div class="as-nav-inner">
          <router-link :to="{ name: 'alerts_dashboard' }" class="as-nav-home" :class="{ active: $route.name === 'alerts_dashboard' }">
            <i class="fa fa-th-large as-nav-home-icon"></i>
            Dashboard
          </router-link>
          <router-link :to="{ name: 'alert_list' }" class="as-nav-sub-item" :class="{ active: $route.name === 'alert_list' }">
            <i class="fa fa-history as-nav-sub-icon"></i>Alert History
          </router-link>
          <router-link :to="{ name: 'delivery_failures' }" class="as-nav-sub-item" :class="{ active: $route.name === 'delivery_failures' }">
            <i class="fa fa-exclamation-triangle as-nav-sub-icon"></i>Delivery Failures
          </router-link>
          <router-link
            v-if="showGroupMessageNav"
            :to="{ name: groupMessageRoute }"
            class="as-nav-sub-item"
            :class="{ active: $route.name === 'group_message_sender' || $route.name === 'group_message_sender_tbn' }"
          >
            <i class="fa fa-bullhorn as-nav-sub-icon"></i>Group Message
          </router-link>
          <router-link
            v-if="showAlertSettingsNav"
            :to="{ name: 'settings', query: { tab: 'tab_alerts' } }"
            class="as-nav-sub-item"
            :class="{ active: $route.name === 'settings' && $route.query.tab === 'tab_alerts' }"
          >
            <i class="fa fa-cog as-nav-sub-icon"></i>Alert Settings
          </router-link>
        </div>
        <button
          class="as-nav-toggle"
          @click="toggleNav"
          :title="navCollapsed ? 'Expand menu' : 'Collapse menu'"
          :aria-label="navCollapsed ? 'Expand menu' : 'Collapse menu'"
        >
          <i class="fa" :class="navCollapsed ? 'fa-chevron-right' : 'fa-chevron-left'"></i>
        </button>
      </div>
      <div class="as-content">
        <router-view />
      </div>
    </div>

  </div>
</template>

<script>
import { mapState } from 'vuex'
import { hasAlertsAccess, hasAlertsHistoryAccess, hasGroupMessageAccess, groupMessageRouteName, isFeatureEnabled } from '../utils/alerts_access'
import horizonNavMixin from '@/nav/horizon_nav_mixin'
import FeatureUpsell from './feature_upsell'

const UPSELL_ITEMS = [
  { label: 'Late alerts', desc: 'Notify drivers and dispatch when bookings or vehicles are running behind schedule.' },
  { label: 'Trip notifications', desc: 'Keep customers and drivers informed with automated pre-trip and post-trip messages.' },
  { label: 'Vehicle activity alerts', desc: 'Detect unplanned movement, inactivity, gateway disconnects, and ELD issues.' },
  { label: 'Group messaging', desc: 'Broadcast urgent updates to drivers and staff from one place.' },
]

export default {
  name: 'alerts_shell',
  components: { FeatureUpsell },
  mixins: [horizonNavMixin],
  data() {
    return {
      navCollapsed: false,
      navUserToggled: false,
      upsellItems: UPSELL_ITEMS,
    }
  },
  computed: {
    ...mapState({
      user: state => state.profile.user,
      viewAs: state => state.profile.view_as,
    }),
    features() {
      return (this.user.features && this.user.features[this.user.tenant]) || {}
    },
    showMarketingPage() {
      if (this.$route.query.upsell) {
        return !hasAlertsAccess(this.features)
      }
      return !hasAlertsHistoryAccess(this.features)
    },
    showGroupMessageNav() {
      return hasGroupMessageAccess(this.user)
    },
    groupMessageRoute() {
      return groupMessageRouteName(this.user)
    },
    isEffectiveAdmin() {
      return this.user.role === 'admin' && this.viewAs !== 'non-admin'
    },
    showAlertSettingsNav() {
      // mirrors both the 'settings' section gate and the nested
      // 'alerts_settings' item gate in nav/registry.js — a user must clear
      // both to actually reach this tab via normal navigation, so the
      // shortcut shouldn't appear for anyone who wouldn't already see it there
      const sectionGate = this.isEffectiveAdmin || this.user.role === 'customer-admin' || this.user.role === 'tbn-admin'
      const itemGate = this.isEffectiveAdmin ||
        isFeatureEnabled(this.features.vehicle_status_management_enabled) ||
        isFeatureEnabled(this.features.alerts_enabled)
      return sectionGate && itemGate
    },
  },
  mounted() {
    this._onResize = () => {
      // Keep the nav responsive to window width unless the user has explicitly toggled it
      if (this.navUserToggled) return
      this.navCollapsed = window.innerWidth < 1600
    }
    window.addEventListener('resize', this._onResize)
    this._onResize()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this._onResize)
  },
  methods: {
    toggleNav() {
      this.navCollapsed = !this.navCollapsed
      this.navUserToggled = true
    },
  },
}
</script>

<style scoped>
/* ── Nav shell (mirrors booking_shell.vue) ──────────────────────────── */
.as-layout {
  display: flex;
  align-items: flex-start;
}

.as-nav-wrapper {
  position: relative;
  display: flex;
  flex-shrink: 0;
}

.as-nav-inner {
  width: 250px;
  min-width: 250px;
  overflow: hidden;
  transition: width 0.22s ease, min-width 0.22s ease;
  background-color: #e8eef5;
  border-right: 2px solid #dee2e6;
  padding: 12px 0;
}

.as-nav-collapsed .as-nav-inner {
  width: 0;
  min-width: 0;
  border-right: none;
  padding: 0;
}

.as-nav-home {
  display: flex;
  align-items: center;
  padding: 10px 16px;
  font-size: 15px;
  font-weight: 600;
  color: #333 !important;
  text-decoration: none !important;
  border-bottom: 2px solid #c8d8e8;
  white-space: nowrap;
  cursor: pointer;
  background: transparent;
  transition: background 0.12s;
}

.as-nav-home:hover {
  background-color: #d0dfee;
  text-decoration: none !important;
}

.as-nav-home.active {
  background-color: steelblue !important;
  color: white !important;
  border-bottom-color: #f0f0f0;
}

.as-nav-home-icon {
  margin-right: 7px;
  font-size: 12px;
}

.as-nav-sub-icon {
  margin-right: 6px;
  width: 14px;
  text-align: center;
}

.as-nav-sub-item {
  display: block;
  padding: 10px 16px;
  font-size: 14px;
  font-weight: 600;
  color: #333 !important;
  text-decoration: none !important;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  border-left: 3px solid transparent;
  transition: background 0.1s, border-color 0.1s;
}

.as-nav-sub-item:hover {
  background-color: #dce8f5;
  text-decoration: none !important;
}

.as-nav-sub-item.active {
  background-color: #dce8f5;
  border-left-color: steelblue;
  color: steelblue !important;
  font-weight: 600;
}

.as-nav-toggle {
  position: absolute;
  top: 12px;
  right: -20px;
  width: 20px;
  height: 36px;
  background: #e8eef5;
  border: 1px solid #c8d8e8;
  border-left: none;
  border-radius: 0 4px 4px 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #4a6785;
  font-size: 11px;
  padding: 0;
  z-index: 10;
  transition: background 0.15s;
}

.as-nav-toggle:hover {
  background: #d0dfee;
  color: steelblue;
}

.as-nav-toggle:focus {
  outline: none;
}

.as-content {
  flex: 1;
  min-width: 0;
  padding-left: 32px;
}

.alerts-shell {
  margin-top: 20px;
}

/* no in-page nav rail to clear (see the template note): the pages inside
   supply their own gutters, so hand the space back to them */
.alerts-shell--flush {
  margin-top: 0;
}

.alerts-shell--flush .as-content {
  padding-left: 0;
}
</style>
